import type { CpcBreakEven } from '@clarity/contract';
import { dec, pct } from '../format.js';

/**
 * CpcBreakeven (clarity-campaign-matching task 13.3, Req 9.3 / 7.4) — the
 * ads-only CPC break-even block, rebuilt from the agreed mock
 * `brand/campaigns-mock.html` (the `#cd-cpc` panel) in the teal-glass world.
 * The mock is the contract.
 *
 * Rendered ONLY when the projection carries a {@link CpcBreakEven} (ads
 * campaigns); discount campaigns pass `cpc: null` and this renders nothing. It
 * draws:
 *  - the break-even headline — the break-even conversion rate `breakEvenCR`, with
 *    the margin per converted order and the CPC bid it was computed at;
 *  - the profit-vs-CR curve from `profitAtCR`, a small chart through the zero
 *    crossing at `breakEvenCR`, with the `projectedCR` marker when known.
 *
 * The curve recomputes live with the CPC slider (13.2): `cpc` reshapes the whole
 * projection server-side, so a new `CpcBreakEven` arrives and the chart redraws.
 */
export function CpcBreakeven({
  cpc,
  currency,
}: {
  cpc: CpcBreakEven;
  currency: string;
}) {
  const clicksPerOrder = cpc.breakEvenCR > 0 ? 1 / cpc.breakEvenCR : 0;

  return (
    <section className="card panel cpc" aria-label="CPC break-even">
      <h3 className="cpc__title">
        CPC break-even
        <span className="pm__hint">
          {' '}
          · this is a paid keyword campaign — you pay per click, not per sale. How many of those
          clicks must convert before the spend pays for itself?
        </span>
      </h3>

      <div className="cpc__grid">
        <div className="cpc__side">
          <div className="cpc-be">
            <span className="cpc-be__lbl">Break-even conversion rate</span>
            <span className="cpc-be__big num">{pct(cpc.breakEvenCR * 100)}</span>
            <span className="cpc-be__sub">
              {dec(clicksPerOrder, 1)} clicks per order · at {dec(cpc.cpc)} {currency}/click
              and {dec(cpc.margin)} {currency} margin/order
            </span>
          </div>
          <p className="cpc__note">
            The full breakdown above shows profit at every bid and conversion rate. This is the
            quick read at your current bid.
          </p>
        </div>

        <div className="cpc__chart">
          <h4 className="cpc__chart-title">Profit vs conversion rate</h4>
          <CpcCurve cpc={cpc} />
        </div>
      </div>
    </section>
  );
}

// ── Profit-vs-CR curve ────────────────────────────────────────────────────────
//
// LOCALE EXCEPTION (Req 9.5): the numbers rendered INSIDE the SVG below — the
// axis-tick percentages, the "projected N%" marker, the "break-even" caption —
// stay plain Latin-digit strings. They are chart furniture positioned by `x()`/
// `y()` at authored pixel coordinates; running them through `Intl` (ar-JO
// numerals / RTL shaping) would desynchronize glyph widths from the computed
// tick positions and is impractical in raw `<text>`. The chart's MEANING is
// carried locale-neutrally by its `role="img"` `aria-label` (which spells the
// break-even and projected rates out as words), and every figure a user reads as
// data — the break-even headline, the sub-line, the stats — is locale-aware in
// the DOM above. The chart mirrors as a whole in RTL via its container.

const W = 360;
const H = 200;
const PAD = 34;
/** Log-x domain bounds (share of clicks that convert): 1% … 50%, as in the mock. */
const CR_MIN = 0.01;
const CR_MAX = 0.5;
const X_TICKS = [0.01, 0.03, 0.05, 0.08, 0.1, 0.15, 0.2, 0.3, 0.5];

/**
 * The profit-vs-conversion-rate sparkline. The x-axis is log-scaled (so the low
 * conversion rates where the curve bends are legible), the y-axis linear through
 * the zero crossing. The teal line is the sampled `profitAtCR`; the amber dashed
 * line + dot mark the break-even CR; the teal dashed line + dot mark the
 * projected CR. One authored `role="img"` with a full-sentence `aria-label`.
 */
function CpcCurve({ cpc }: { cpc: CpcBreakEven }) {
  // Densify the curve for a smooth path from the margin/cpc the block carries,
  // bounded by the sampled points so it stays faithful to the server figures.
  const samples = buildCurve(cpc);
  const profits = samples.map((p) => p.profit);
  const yMin = Math.min(...profits, -cpc.margin);
  const yMax = Math.max(...profits, cpc.margin * 0.5, 2);

  const x = (cr: number) =>
    PAD + ((Math.log(clamp(cr)) - Math.log(CR_MIN)) / (Math.log(CR_MAX) - Math.log(CR_MIN))) * (W - PAD - 8);
  const y = (v: number) => H - PAD - ((v - yMin) / (yMax - yMin || 1)) * (H - PAD - 10);

  const line = samples
    .map((p, i) => `${i ? 'L' : 'M'}${x(p.cr).toFixed(1)},${y(p.profit).toFixed(1)}`)
    .join(' ');
  const zeroY = y(0);
  const area = `M${x(CR_MIN).toFixed(1)},${zeroY.toFixed(1)} ${line.replace(/^M/, 'L')} L${x(CR_MAX).toFixed(1)},${zeroY.toFixed(1)} Z`;
  const beX = x(cpc.breakEvenCR);
  const projCR = cpc.projectedCR;
  const gradId = 'cpc-pfill';

  const label =
    `Profit per order against conversion rate; crosses break-even at ${(cpc.breakEvenCR * 100).toFixed(1)} percent` +
    (projCR != null ? `, projected ${(projCR * 100).toFixed(0)} percent.` : '.');

  return (
    <svg className="cpc__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--teal-400)" stopOpacity="0.28" />
          <stop offset="1" stopColor="var(--teal-400)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Zero / break-even baseline. */}
      <line
        x1={PAD}
        y1={zeroY.toFixed(1)}
        x2={W - 8}
        y2={zeroY.toFixed(1)}
        stroke="var(--warn)"
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity="0.7"
      />
      <text x={PAD + 2} y={(zeroY - 5).toFixed(1)} fill="var(--warn)" fontSize="9" className="num">
        break-even
      </text>

      {/* Filled area + the profit curve. */}
      <path d={area} fill={`url(#${gradId})`} />
      <path d={line} fill="none" stroke="var(--teal-300)" strokeWidth="2" strokeLinejoin="round" />

      {/* Break-even marker. */}
      <line
        x1={beX.toFixed(1)}
        y1="10"
        x2={beX.toFixed(1)}
        y2={H - PAD}
        stroke="var(--fg-faint)"
        strokeWidth="1"
        strokeDasharray="2 3"
        opacity="0.5"
      />
      <circle cx={beX.toFixed(1)} cy={zeroY.toFixed(1)} r="3.5" fill="var(--warn)" />

      {/* Projected-CR marker (teal), when history gives an estimate. */}
      {projCR != null && (
        <>
          <line
            x1={x(projCR).toFixed(1)}
            y1="10"
            x2={x(projCR).toFixed(1)}
            y2={H - PAD}
            stroke="var(--teal-300)"
            strokeWidth="1"
            strokeDasharray="2 3"
            opacity="0.65"
          />
          <circle
            cx={x(projCR).toFixed(1)}
            cy={y(profitAt(cpc, projCR)).toFixed(1)}
            r="4"
            fill="var(--teal-300)"
          />
          <text
            x={x(projCR).toFixed(1)}
            y="18"
            fill="var(--teal-200)"
            fontSize="9"
            textAnchor="middle"
            className="num"
          >
            projected {(projCR * 100).toFixed(0)}%
          </text>
        </>
      )}

      {/* X-axis conversion-rate ticks. */}
      {X_TICKS.map((t) => (
        <text
          key={t}
          x={x(t).toFixed(1)}
          y={H - PAD + 16}
          fill="var(--fg-faint)"
          fontSize="9"
          textAnchor="middle"
          className="num"
        >
          {Math.round(t * 100)}%
        </text>
      ))}
    </svg>
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

function clamp(cr: number): number {
  return Math.min(CR_MAX, Math.max(CR_MIN, cr));
}

/** Profit per order at a conversion rate: `margin − (1/cr) × cpc`. */
function profitAt(cpc: CpcBreakEven, cr: number): number {
  return cpc.margin - (1 / cr) * cpc.cpc;
}

/**
 * A dense, smooth curve over the log-x domain. The block's `margin`/`cpc` define
 * the exact profit function (`profit(cr) = margin − (1/cr) × cpc`) the server
 * sampled into `profitAtCR`; densifying from the same function keeps the drawn
 * line faithful while the sampled points anchor it (they lie on this curve).
 */
function buildCurve(cpc: CpcBreakEven): Array<{ cr: number; profit: number }> {
  const points: Array<{ cr: number; profit: number }> = [];
  for (let i = 1; i <= 50; i += 1) {
    const cr = i / 100;
    if (cr < CR_MIN || cr > CR_MAX) continue;
    points.push({ cr, profit: profitAt(cpc, cr) });
  }
  return points;
}
